import { useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { S, Icon } from '../regureStyles';
import { inputPhoneMasking, returnOnlyAlphabets, returnOnlyAlphabetsWithSpace, unMasking, validateEmail, validatePhone } from '../../utils/validators';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const toCarrier = c => ({
  id: c._id || c.id,
  name: c.name,
  code: c.code || '',
  products: c.products || [],
  activePolicies: c.activePolicies || 0,
  monthlyPremium: c.monthlyPremium || 0,
  status: c.status || 'active',
  contact: c.contact,
  email: c.email,
  phone: c.phone,
  address: c.address,
  city: c.city,
  canton: c.canton,
  bordereau: c.bordereau || 'Pending',
  bordereauHistory: c.bordereauHistory || [],
  bordereauxConfig: c.bordereauxConfig || { frequency: 'monthly', format: 'xlsx', dayOfMonth: 5, autoSend: false, recipients: [] },
  active: c.active !== false,
  createdAt: c.createdAt,
});

// ─── Input helper ─────────────────────────────────────────
const Field = ({ label, value, onChange, type = 'text', placeholder }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{ fontSize: 11, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 5 }}>{label}</div>
    <input
      type={type}
      value={value || ''}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder || ''}
      style={{
        width: '100%',
        padding: '9px 12px',
        background: S.bgCard,
        border: `1px solid ${S.borderLight}`,
        borderRadius: 8,
        fontSize: 13,
        color: S.textPrimary,
        outline: 'none',
        boxSizing: 'border-box',
      }}
    />
  </div>
);

const AddCarrierModal = ({ onClose, onCreate }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', code: '', contact: '', email: '', phone: '', city: '', canton: '', products: '' });
  const [saving, setSaving] = useState(false);
  const set = k => v =>
    setForm(f => ({
      ...f,
      [k]:
        k === 'phone'
          ? inputPhoneMasking(v)
          : k === 'name' || k === 'contact'
            ? returnOnlyAlphabetsWithSpace(v)
            : k === 'code' || k === 'canton'
              ? returnOnlyAlphabets(v.toUpperCase())
              : v,
    }));

  const handleSubmit = async () => {
    if (!form.name || !validateEmail(form.email) || !validatePhone(unMasking(form.phone))) return;
    setSaving(true);
    try {
      if (form.name) {
      }
      const resp = await axios.post(
        `${BASE_URL}/risk-carriers`,
        {
          name: form.name,
          code: form.code,
          contact: form.contact,
          email: form.email,
          phone: unMasking(form.phone),
          city: form.city,
          canton: form.canton,
          products: form.products
            .split(',')
            .map(s => s.trim())
            .filter(Boolean),
        },
        { withCredentials: true },
      );
      if (resp.data?.data) onCreate(toCarrier(resp.data.data));
      onClose();
    } catch {
      /* silent */
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div style={{ background: '#fff', borderRadius: 20, padding: 28, width: 520, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: S.textPrimary }}>{t('screens.riskCarriers.modalTitle', { defaultValue: 'Add Risk Carrier' })}</div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}>
            <Icon name='x' color={S.textMuted} size={18} />
          </button>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <Field label={t('screens.riskCarriers.modalName',    { defaultValue: 'Carrier Name *' })} value={form.name}    onChange={set('name')}    placeholder={t('screens.riskCarriers.modalNamePh',    { defaultValue: 'Zurich Insurance Group AG' })} />
          </div>
          <Field label={t('screens.riskCarriers.modalCode',    { defaultValue: 'Code' })}         value={form.code}    onChange={set('code')}    placeholder={t('screens.riskCarriers.modalCodePh',    { defaultValue: 'ZURI' })} />
          <Field label={t('screens.riskCarriers.modalContact', { defaultValue: 'Contact Name' })} value={form.contact} onChange={set('contact')} placeholder={t('screens.riskCarriers.modalContactPh', { defaultValue: 'Dr. Rolf Baumgartner' })} />
          <Field label={t('screens.riskCarriers.modalEmail',   { defaultValue: 'Email' })}        value={form.email}   onChange={set('email')}   type='email' placeholder={t('screens.riskCarriers.modalEmailPh', { defaultValue: 'contact@carrier.com' })} />
          <Field label={t('screens.riskCarriers.modalPhone',   { defaultValue: 'Phone' })}        value={form.phone}   onChange={set('phone')}   placeholder={t('screens.riskCarriers.modalPhonePh',   { defaultValue: '+41 44 123 45 67' })} />
          <Field label={t('screens.riskCarriers.modalCity',    { defaultValue: 'City' })}         value={form.city}    onChange={set('city')}    placeholder={t('screens.riskCarriers.modalCityPh',    { defaultValue: 'Zurich' })} />
          <Field label={t('screens.riskCarriers.modalCanton',  { defaultValue: 'Canton' })}       value={form.canton}  onChange={set('canton')}  placeholder={t('screens.riskCarriers.modalCantonPh',  { defaultValue: 'ZH' })} />
          <div style={{ gridColumn: '1 / -1' }}>
            <Field label={t('screens.riskCarriers.modalProducts', { defaultValue: 'Products (comma-separated)' })} value={form.products} onChange={set('products')} placeholder={t('screens.riskCarriers.modalProductsPh', { defaultValue: 'Professional Liability, Daily Sickness' })} />
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
          <button
            onClick={onClose}
            style={{
              padding: '9px 18px',
              background: S.bgCard,
              border: `1px solid ${S.borderLight}`,
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 500,
              color: S.textSecondary,
              cursor: 'pointer',
            }}
          >
            {t('screens.riskCarriers.modalCancel', { defaultValue: 'Cancel' })}
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || Object.values(form).some(value => !value || value.trim() === '')}
            style={{
              padding: '9px 22px',
              background: `linear-gradient(135deg, ${S.burgundy}, ${S.burgundy2})`,
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              opacity: saving || Object.values(form).some(value => !value || value.trim() === '') ? 0.6 : 1,
            }}
          >
            {saving ? t('screens.riskCarriers.modalBtnSaving', { defaultValue: 'Adding…' }) : t('screens.riskCarriers.modalBtnAdd', { defaultValue: 'Add Carrier' })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddCarrierModal;
